import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Moon, Sun, Github } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';

const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { mode, toggleMode } = useTheme();
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/projects', label: 'Projects' },
    { path: '/philosophy', label: 'Philosophy' },
    { path: '/license', label: 'License' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-opacity-90" style={{ backgroundColor: 'var(--color-bg)' }}>
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <motion.div
              className="text-2xl font-theme font-bold text-gradient"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              theweebcoders
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`
                  relative px-3 py-2 rounded-md text-sm font-medium transition-all
                  ${isActive(link.path)
                    ? 'text-gradient'
                    : 'hover:scale-105'
                  }
                `}
                style={{
                  color: isActive(link.path) ? undefined : 'var(--color-text)'
                }}
              >
                {link.label}
                {isActive(link.path) && (
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-0.5"
                    style={{ background: 'var(--gradient-primary)' }}
                    layoutId="navbar-indicator"
                  />
                )}
              </Link>
            ))}
          </div>

          {/* Right side buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Theme toggle */}
            <button
              onClick={toggleMode}
              className="p-2 rounded-lg hover:bg-opacity-10 transition-all"
              style={{ backgroundColor: 'var(--color-primary)' + '20' }}
              aria-label={`Switch to ${mode === 'dark' ? 'light' : 'dark'} mode`}
            >
              {mode === 'dark' ? (
                <Sun size={20} style={{ color: 'var(--color-primary)' }} />
              ) : (
                <Moon size={20} style={{ color: 'var(--color-primary)' }} />
              )}
            </button>

            {/* GitHub link */}
            <a
              href="https://github.com/theweebcoders"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg hover:bg-opacity-10 transition-all"
              style={{ backgroundColor: 'var(--color-primary)' + '20' }}
              aria-label="Visit our GitHub"
            >
              <Github size={20} style={{ color: 'var(--color-primary)' }} />
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg"
            style={{ color: 'var(--color-primary)' }}
            aria-label="Toggle mobile menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden overflow-hidden"
            >
              <div className="px-2 pt-2 pb-3 space-y-1">
                {navLinks.map(link => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`
                      block px-3 py-2 rounded-md text-base font-medium
                      ${isActive(link.path) ? 'text-gradient' : ''}
                    `}
                    style={{
                      color: isActive(link.path) ? undefined : 'var(--color-text)',
                      backgroundColor: isActive(link.path) ? 'var(--color-primary)' + '10' : undefined
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="flex items-center space-x-2 px-3 py-2">
                  <button
                    onClick={toggleMode}
                    className="flex-1 p-2 rounded-lg transition-all"
                    style={{ backgroundColor: 'var(--color-primary)' + '20' }}
                  >
                    {mode === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                  </button>
                  <a
                    href="https://github.com/theweebcoders"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 p-2 rounded-lg transition-all text-center"
                    style={{ backgroundColor: 'var(--color-primary)' + '20' }}
                  >
                    <Github size={20} className="inline" />
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Header;
